import { z } from "zod/v3";

// RFC-213: the Infra Compliance Assessment — the automatable subset of
// compliance, computed server-side from native check-run results (never CES).
// Deliberately NO readiness field: `passRate` + `verdict` judge only checks a
// scanner can decide; renderers must pair them with the "automated checks —
// not a readiness score" qualifier.

export const SetupAssessmentVerdictSchema = z.enum([
  "solid",
  "needs_attention",
  "at_risk",
]);
export type SetupAssessmentVerdict = z.infer<
  typeof SetupAssessmentVerdictSchema
>;

export const SetupAssessmentGapSchema = z
  .object({
    checkId: z.string(),
    provider: z.string(),
    title: z.string(),
    detail: z.string(),
    severity: z.enum(["high", "medium"]),
    // "relates to" — applicability hasn't run; never claim a control is failed.
    relatedControlRefs: z.array(z.string()),
  })
  .strict();
export type SetupAssessmentGap = z.infer<typeof SetupAssessmentGapSchema>;

export const SetupAssessmentProviderSchema = z
  .object({
    slug: z.string(),
    displayName: z.string(),
    passing: z.number(),
    failing: z.number(),
    autoNA: z.number(),
    inconclusive: z.number().optional(),
    inconclusiveReasons: z.array(z.object({ checkId: z.string(), title: z.string(), reason: z.string() }).strict()).optional(),
    resourcesDiscovered: z.array(
      z.object({ type: z.string(), count: z.number() }).strict(),
    ),
  })
  .strict();
export type SetupAssessmentProvider = z.infer<
  typeof SetupAssessmentProviderSchema
>;

/**
 * RFC-236 §3.5 — the repository as an assessment input, derived from the
 * latest SourceScanRun (SARIF + IaC inventory the CLI uploaded; never source).
 * Null until `screenata scan source` has run for the workspace.
 */
export const SetupAssessmentRepoSchema = z
  .object({
    repoName: z.string(),
    commitSha: z.string(),
    scannedAt: z.string(),
    filesScanned: z.number(),
    /** Unreadable or over-size files the scanner could not read; a pass is not evidence of absence there. */
    filesSkipped: z.number(),
    findingsCount: z.number(),
    iacResourcesCount: z.number(),
    /** SARIF levels of the ingested findings; zeros when the run stored none. */
    byLevel: z.object({ error: z.number(), warning: z.number(), note: z.number() }).strict(),
  })
  .strict();
export type SetupAssessmentRepo = z.infer<typeof SetupAssessmentRepoSchema>;

export const SetupAssessmentDTOSchema = z
  .object({
    generatedAt: z.string(),
    /**
     * True once at least one posture scan exists. Distinguishes "never
     * scanned" (renderers must show the connect prompt — never a fabricated
     * posture) from "scanned but nothing decided".
     */
    hasScans: z.boolean(),
    /** False while connected providers still have runs in flight. */
    scanComplete: z.boolean(),
    totals: z
      .object({
        checksRun: z.number(),
        /** checksRun - autoNA */
        applicable: z.number(),
        passing: z.number(),
        failing: z.number(),
        autoNA: z.number(),
        /** passing / applicable as a 0-100 integer — the ONLY percentage. */
        passRate: z.number(),
        /**
         * Derived server-side from passRate + high-severity fail count.
         * Judges ONLY the automatable subset.
         */
        verdict: SetupAssessmentVerdictSchema,
      })
      .strict(),
    providers: z.array(SetupAssessmentProviderSchema),
    topGaps: z.array(SetupAssessmentGapSchema),
    autoNAReasons: z.array(
      z
        .object({ title: z.string(), naReason: z.string(), count: z.number() })
        .strict(),
    ),
    repo: SetupAssessmentRepoSchema.nullable(),
  })
  .strict();
export type SetupAssessmentDTO = z.infer<typeof SetupAssessmentDTOSchema>;
